// #PO_FILES_SHARED_V1 (2026-09-28) — upload / open PO invoice files in the private 'po-files' bucket.
// Same bucket + path scheme PMDashboard's #PO_INV_FILES_V1 uses, shared so AP (and its Invoice Inbox)
// can attach and open the same files.
export const PO_BUCKET = 'po-files';
// #PO_DOC_VIEWER_V1 (2026-10-15) — Bill: "Pulling POs from AP reader fires off into a WEB PDF… each one you open, opens a
// new browser window." Scans and attachments now open in ONE viewer panel on top of DOPE (Esc / ✕ closes it, opening the
// next file replaces it) — no stack of browser tabs. "⬇ Download" saves the file so it opens in Adobe (or whatever the PC
// uses for PDFs); "↗ New tab" is still there when a second screen is wanted.
const DV_ID = 'dope-doc-viewer';
export function showDocViewer(name, getUrl) {
  if (typeof document === 'undefined') return;
  let el = document.getElementById(DV_ID);
  if (el) el.remove();
  el = document.createElement('div'); el.id = DV_ID;
  el.style.cssText = 'position:fixed;inset:0;z-index:100000;background:rgba(0,0,0,.62);display:flex;align-items:center;justify-content:center;font-family:inherit';
  // #PRINT_VIEW_V1 (2026-10-23) — on a phone the viewer fills the screen and the bar sits below the notch so ✕ Close is always reachable
  const phone = (() => { try { return window.matchMedia('(pointer:coarse)').matches || window.innerWidth < 760; } catch { return false; } })();
  const box = document.createElement('div');
  box.style.cssText = phone ? 'position:fixed;inset:0;background:#1B1D21;display:flex;flex-direction:column;overflow:hidden' : 'width:min(1100px,94vw);height:92vh;background:#1B1D21;border:1px solid #3A3D42;border-radius:10px;display:flex;flex-direction:column;overflow:hidden;box-shadow:0 18px 50px #000b';
  const bar = document.createElement('div');
  bar.style.cssText = 'display:flex;align-items:center;gap:8px;padding:8px 12px;border-bottom:1px solid #3A3D42;color:#EDEEF0;font-size:13px;font-weight:700;flex:0 0 auto' + (phone ? ';padding-top:calc(8px + env(safe-area-inset-top, 0px))' : '');
  const ttl = document.createElement('div'); ttl.textContent = '📎 ' + (name || 'file');
  ttl.style.cssText = 'flex:1 1 auto;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap';
  const mk = (label, title) => { const a = document.createElement('a'); a.textContent = label; a.title = title; a.style.cssText = 'color:#FFB020;border:1px solid #FFB02088;border-radius:6px;padding:4px 10px;font-size:12px;font-weight:800;text-decoration:none;cursor:pointer;white-space:nowrap'; return a; };
  const dl = mk('⬇ Download', 'Save the file — it opens in Adobe (or your PC’s PDF program)');
  const nt = mk('↗ New tab', 'Open in a separate browser tab');
  nt.target = '_blank'; nt.rel = 'noopener noreferrer';
  const x = document.createElement('button'); x.type = 'button'; x.textContent = phone ? '✕ Close' : '✕ Close (Esc)';
  x.style.cssText = 'background:transparent;border:1px solid #3A3D42;color:#EDEEF0;border-radius:6px;padding:4px 10px;font-size:12px;cursor:pointer;white-space:nowrap';
  bar.append(ttl, dl, nt, x);
  if (phone) nt.style.display = 'none';   // #PRINT_VIEW_V1 — a new tab in the home-screen app has no way back
  const body = document.createElement('div'); body.style.cssText = 'flex:1 1 auto;min-height:0;background:#2A2C30;display:flex;align-items:center;justify-content:center;color:#9AA0A8;font-size:13px';
  body.textContent = 'Loading…';
  box.append(bar, body); el.append(box); document.body.append(el);
  const close = () => { try { el.remove(); } catch {} document.removeEventListener('keydown', onKey, true); };
  const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); close(); } };
  document.addEventListener('keydown', onKey, true);
  x.onclick = close; el.onclick = (e) => { if (e.target === el) close(); };
  Promise.resolve().then(getUrl).then((url) => {
    if (!document.getElementById(DV_ID) || el.parentNode == null) return;
    const sep = url.includes('?') ? '&' : '?';
    dl.href = `${url}${sep}download=${encodeURIComponent(name || 'file')}`; nt.href = url;
    body.textContent = '';
    if (/\.(png|jpe?g|gif|webp|bmp)$/i.test(name || '')) {
      const img = document.createElement('img'); img.src = url; img.alt = name || '';
      img.style.cssText = 'max-width:100%;max-height:100%;object-fit:contain';
      body.append(img);
    } else if (/\.(msg|eml|docx?|xlsx?|csv|zip)$/i.test(name || '')) {
      body.textContent = 'This file type can’t be shown here — use ⬇ Download.';
    } else {
      const fr = document.createElement('iframe'); fr.src = url; fr.title = name || 'file';
      fr.style.cssText = 'width:100%;height:100%;border:0;background:#fff';
      body.append(fr);
    }
  }).catch((e) => { body.textContent = 'Could not open the file: ' + ((e && e.message) || e); });
}
export function makePoFiles({ supaUrl, supaKey, getToken }) {
  const hdr = async (extra) => {
    const tok = getToken ? await getToken() : null;
    if (!tok) throw new Error('Your sign-in has expired — reload and try again.');
    return { apikey: supaKey, Authorization: `Bearer ${tok}`, ...(extra || {}) };
  };
  const upload = async (file, folder) => {
    if (!supaUrl || !supaKey) throw new Error('Storage not configured');
    if (file.size > 25 * 1024 * 1024) throw new Error(`${file.name} is over the 25 MB limit.`);
    const safe = String(file.name || 'file').replace(/[^A-Za-z0-9._-]+/g, '_').slice(-80);
    const path = `${String(folder || 'inbox').replace(/[^A-Za-z0-9_/-]/g, '')}/${Date.now()}-${safe}`;
    const res = await fetch(`${supaUrl}/storage/v1/object/${PO_BUCKET}/${path}`, {
      method: 'POST', headers: await hdr({ 'Content-Type': file.type || 'application/octet-stream', 'x-upsert': 'true' }), body: file });
    if (!res.ok) { let d = ''; try { const j = await res.json(); d = j.message || j.error || ''; } catch {} throw new Error(`Upload failed (${res.status}) ${d}`.trim()); }
    return { path, name: file.name || safe, type: file.type || '', size: file.size || 0, at: new Date().toISOString() };
  };
  const url = async (f) => {   // #PO_PACKET_V1 — a one-hour link to one file (used by open and by the PO + invoices print)
    const res = await fetch(`${supaUrl}/storage/v1/object/sign/${PO_BUCKET}/${f.path}`, {
      method: 'POST', headers: await hdr({ 'Content-Type': 'application/json' }), body: JSON.stringify({ expiresIn: 3600 }) });
    const j = await res.json();
    if (!res.ok || !j.signedURL) throw new Error(j.message || j.error || `HTTP ${res.status}`);
    return `${supaUrl}/storage/v1${j.signedURL}`;
  };
  const open = async (f) => { showDocViewer(f && f.name, () => url(f)); };   // #PO_DOC_VIEWER_V1
  const remove = async (f) => {   // #AP_READ_V1 — Discard really deletes the file
    if (!f || !f.path) return;
    await fetch(`${supaUrl}/storage/v1/object/${PO_BUCKET}`, { method: 'DELETE', headers: await hdr({ 'Content-Type': 'application/json' }), body: JSON.stringify({ prefixes: [f.path] }) });
  };
  // #AP_READ_V1 — Claude reads the invoice server-side (edge function read-invoice); only fields come back
  const read = async (f) => {
    const res = await fetch(`${supaUrl}/functions/v1/read-invoice`, { method: 'POST', headers: await hdr({ 'Content-Type': 'application/json' }), body: JSON.stringify({ path: f.path }) });
    const j = await res.json().catch(() => ({}));
    if (!res.ok || j.error) throw new Error(j.error || `read failed (${res.status})`);
    return j;
  };
  // #AP_SCAN_PICKUP_V1 (2026-10-02) — scans the pickup job (edge function ap-scan-pickup) already copied in from
  // the AP Scans SharePoint site wait one-per-key as `po-scan-<itemId>`; the AP screen pulls them into its inbox
  // and clears the keys. The server only ever adds keys, the AP screen only ever deletes them — no clobbering.
  const scanRows = async () => {
    const res = await fetch(`${supaUrl}/rest/v1/app_storage?key=like.po-scan-*&select=key,value`, { headers: await hdr() });
    if (!res.ok) throw new Error(`scan queue (${res.status})`);
    const rows = await res.json();
    return (Array.isArray(rows) ? rows : []).map(r => { try { return { key: r.key, row: JSON.parse(r.value) }; } catch { return null; } }).filter(x => x && x.row && x.row.id && x.row.file);
  };
  const scanClear = async (keys) => {
    if (!keys || !keys.length) return;
    await fetch(`${supaUrl}/rest/v1/app_storage?key=in.(${keys.map(k => encodeURIComponent(`"${k}"`)).join(',')})`, { method: 'DELETE', headers: await hdr() });
  };
  const scanCheck = async () => {   // "Check scanner now" — same job the 5-minute schedule runs
    const res = await fetch(`${supaUrl}/functions/v1/ap-scan-pickup`, { method: 'POST', headers: await hdr({ 'Content-Type': 'application/json' }), body: JSON.stringify({ action: 'run' }) });
    const j = await res.json().catch(() => ({}));
    if (!res.ok && !j.error) throw new Error(`check failed (${res.status})`);
    return j;
  };
  return { upload, open, url, remove, read, scanRows, scanClear, scanCheck };
}
export const filesFromDrop = (e) => {
  const dt = e.dataTransfer || {};
  let files = Array.from(dt.files || []);
  if (!files.length && dt.items) files = Array.from(dt.items).filter(i => i.kind === 'file').map(i => i.getAsFile()).filter(Boolean);
  return files;
};
// #AP_INBOX_V1 — read PO # / invoice # / amount out of a scanned file's name,
// e.g. "BM460002 Vermeer INV 88123 1053.43.pdf"
export function parseInvoiceName(name, poSet) {
  const base = String(name || '').replace(/\.[A-Za-z0-9]{2,4}$/, '');
  const toks = base.toUpperCase().split(/[^A-Z0-9]+/).filter(Boolean);
  let po = toks.find(t => /^[A-Z]{2}\d{6}$/.test(t) && poSet.has(t)) || toks.find(t => /^[A-Z]{2}\d{5,6}$/.test(t) && poSet.has(t));
  let weak = false;
  if (!po) { po = toks.find(t => /^\d{4,5}$/.test(t) && poSet.has(t)); weak = !!po; }   // Bob's legacy numbers — flag to double-check
  const inv = (base.match(/(?:INV(?:OICE)?|#)\s*[-#:.]?\s*([A-Za-z0-9-]{3,})/i) || [])[1] || '';
  const am = (base.match(/\$?\s*(\d{1,3}(?:,\d{3})+|\d+)\.(\d{2})(?!\d)/g) || []).pop();
  const amount = am ? Number(am.replace(/[^0-9.]/g, '')) : null;
  return { po: po || '', weak, invoiceNum: inv && inv.toUpperCase() !== (po || '') ? inv : '', amount };
}

// #AP_READ_V1 — match what was read off an invoice to a PO.
const normSup = (x) => String(x || '').toUpperCase().replace(/&/g, 'AND').replace(/[^A-Z0-9 ]/g, ' ').replace(/\b(INC|LLC|CO|COMPANY|CORP|LTD|THE|OF)\b/g, ' ').replace(/\s+/g, ' ').trim();
const supScore = (a, b) => {
  const A = normSup(a), B = normSup(b); if (!A || !B) return 0;
  if (A === B) return 1;
  const ca = A.replace(/ /g, ''), cb = B.replace(/ /g, '');
  if (ca.startsWith(cb) || cb.startsWith(ca)) return 0.9;
  const ta = new Set(A.split(' ').filter(t => t.length > 2)), tb = B.split(' ').filter(t => t.length > 2);
  const hit = tb.filter(t => ta.has(t)).length;
  return hit ? Math.min(0.85, hit / Math.max(ta.size, tb.length) + 0.3) : 0;
};
// #AP_LEGACY_MATCH_V1 (2026-10-07) — Bill: "Scanning not picking up recent POs… allow invoices to attach or recognize
// OLD & New POs." Invoices for Bob's last old-number POs (through 11549) keep arriving for a few weeks after his switch to
// BM46 numbers. Bob's log already carries the invoice # on each of those POs, so a scan is matched two ways now: the old
// PO # printed on it (trusted when the supplier and the invoice # or amount agree with his log), or — when no PO # is
// printed — the invoice # itself found in his log. Bob writes his vendor initials after the number ("4050287724 STM"),
// so the number read off the scan is the front of his.
export const invKey = (x) => String(x || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
export const invSame = (a0, b0) => {
  const a = invKey(a0), b = invKey(b0); if (!a || !b) return false;
  if (a === b) return true;
  const [s, l] = a.length <= b.length ? [a, b] : [b, a];
  return s.length >= 5 && l.includes(s) && l.length - s.length <= 6;
};
const supLoose = (a, b) => {
  if (supScore(a, b) >= 0.5) return true;
  const ca = normSup(a).replace(/ /g, ''), cb = normSup(b).replace(/ /g, ''); let n = 0;
  while (n < ca.length && n < cb.length && ca[n] === cb[n]) n++;
  return n >= 5;   // "O'Reilly Auto Parts" = "O'reillys"
};
export function matchInvoiceToPo(read, all, poSet) {
  const cand = [...(read.po_numbers || [])].map(x => String(x).toUpperCase().replace(/[^A-Z0-9]/g, ''));
  const po = cand.find(c => poSet.has(c)) || cand.map(c => c.replace(/^PO/, '')).find(c => poSet.has(c)) || '';
  const amt = read.total != null ? Number(read.total) : null;
  const inLog = (r) => (r.invoices || []).some(i => i && invSame(read.invoice_number, i.invoiceNum));
  const byInvNum = () => {   // is this invoice # already in Bob's log?
    if (invKey(read.invoice_number).length < 5) return null;
    const hits = all.filter(r => r.source === 'imported' && inLog(r));
    const ok = hits.filter(r => supLoose(read.vendor, r.supplier));
    if (ok.length === 1) return { po: String(ok[0].po), how: "invoice # is in Bob's log", suggest: [], strong: true };
    if (!ok.length && hits.length === 1) return { po: '', how: 'suggested', suggest: [{ po: String(hits[0].po), sc: 60, supplier: hits[0].supplier, estimate: hits[0].actual }] };
    return null;
  };
  if (po && !/^[A-Z]{2}\d{6}$/.test(po)) {   // old-format number(s) printed on the invoice — take the one Bob's log agrees with
    const olds = [...new Set([...cand, ...cand.map(c => c.replace(/^PO/, ''))])].filter(c => poSet.has(c) && !/^[A-Z]{2}\d{6}$/.test(c));
    const sure = olds.find(c => { const r = all.find(x => String(x.po).toUpperCase() === c && x.source === 'imported');
      return !!r && supLoose(read.vendor, r.supplier) && (inLog(r) || (amt != null && r.actual != null && Math.abs(Number(r.actual) - amt) < 0.005)); });
    if (sure) return { po: sure, how: "old PO # on invoice · agrees with Bob's log", suggest: [], strong: true };
    const viaInv = byInvNum();
    if (viaInv && viaInv.strong) return viaInv;   // the # printed was not this invoice's PO, but Bob logged the invoice under another
    return { po, how: 'PO # on invoice', suggest: [], strong: false };
  }
  if (po) return { po, how: 'PO # on invoice', suggest: [] };
  { const viaInv = byInvNum(); if (viaInv) return viaInv; }
  const invDate = read.invoice_date || '';
  const open = all.filter(r => r.source !== 'imported');   // new-system POs only for suggestions
  const scored = open.map(r => {
    let sc = 0;
    const ss = supScore(read.vendor, r.supplier); sc += ss * 50;
    if (amt != null && r.estimate != null && r.estimate > 0) { const d = Math.abs(amt - r.estimate) / r.estimate; sc += d <= 0.02 ? 35 : d <= 0.1 ? 25 : d <= 0.25 ? 12 : 0; }
    if (invDate && r.date && invDate >= r.date) sc += 5;
    const refs = [...(read.job_numbers || []), ...(read.unit_numbers || [])].map(x => String(x).toUpperCase());
    if (refs.length && refs.includes(String(r.ducOrUnit || '').toUpperCase())) sc += 25;
    return { po: r.po, sc, supplier: r.supplier, estimate: r.estimate };
  }).filter(x => x.sc >= 40).sort((a, b) => b.sc - a.sc).slice(0, 3);
  return { po: '', how: scored.length ? 'suggested' : 'no PO', suggest: scored };
}
